/**
 * Training Matrix Page - Sprint 6
 * Sprint 11A: Updated with Layout component and Avida branding
 * Shows all personnel with their training compliance status in a matrix format
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { trainingAPI } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  ArrowLeft,
  RefreshCw,
  Download,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
} from "lucide-react";

const STATUS_BADGE_COLORS = {
  Valid: "bg-green-100 text-green-700 border-green-200",
  Expired: "bg-red-100 text-red-700 border-red-200",
  ExpiringSoon: "bg-yellow-100 text-yellow-700 border-yellow-300",
  Missing: "bg-gray-100 text-gray-500 border-gray-200",
  "N/A": "bg-gray-50 text-gray-400 border-gray-100",
};

const TYPE_LABELS = {
  Caregiver: "Caregiver",
  RN: "RN",
  OfficeStaff: "Office Staff",
  Contractor: "Contractor",
};

const TrainingMatrixPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [matrixData, setMatrixData] = useState({ rows: [], requirement_codes: [] });
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { as_of_date: asOfDate };
      if (typeFilter !== "all") params.personnel_type = typeFilter;
      if (statusFilter !== "all") params.personnel_status = statusFilter;
      
      const [matrixRes, reqRes] = await Promise.all([
        trainingAPI.getMatrix(params),
        trainingAPI.getRequirements(),
      ]);
      
      setMatrixData(matrixRes.data);
      setRequirements(reqRes.data.requirements || []);
    } catch (error) {
      toast.error("Failed to load training matrix");
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter, asOfDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = { as_of_date: asOfDate };
      if (typeFilter !== "all") params.personnel_type = typeFilter;
      if (statusFilter !== "all") params.personnel_status = statusFilter;
      
      const response = await trainingAPI.exportMatrixCsv(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `training_matrix_${asOfDate}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported successfully");
    } catch (error) {
      toast.error("Failed to export CSV");
    } finally {
      setExporting(false);
    }
  };

  // Get requirement name by code
  const getReqName = (code) => {
    const req = requirements.find(r => r.code === code);
    return req ? req.name : code;
  };

  // Calculate stats
  const stats = {
    total: matrixData.rows.length,
    compliant: matrixData.rows.filter(r => r.is_compliant).length,
    nonCompliant: matrixData.rows.filter(r => !r.is_compliant).length,
  };

  const renderStatusBadge = (reqData, code) => {
    if (!reqData || !reqData.status) {
      return (
        <Badge variant="outline" className={STATUS_BADGE_COLORS["N/A"]}>
          N/A
        </Badge>
      );
    }

    const status = reqData.status;
    const expDate = reqData.expiration_date;
    
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger>
            <Badge variant="outline" className={STATUS_BADGE_COLORS[status] || STATUS_BADGE_COLORS["N/A"]}>
              {status === "Valid" && <CheckCircle className="h-3 w-3 mr-1" />}
              {status === "Expired" && <XCircle className="h-3 w-3 mr-1" />}
              {status === "ExpiringSoon" && <AlertTriangle className="h-3 w-3 mr-1" />}
              {status === "Missing" && "-"}
              {status !== "Missing" && status.replace("ExpiringSoon", "Soon")}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <p className="font-medium">{getReqName(code)}</p>
            {expDate && <p className="text-xs">Expires: {expDate}</p>}
            {status === "Missing" && <p className="text-xs">Not completed</p>}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  return (
    <Layout title="Training Matrix">
      <div data-testid="training-matrix-page">
        {/* Back button */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate("/personnel")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Personnel
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-2xl">
          <Card className="bg-white border-gray-200">
            <CardContent className="p-4">
              <div className="text-2xl font-bold text-gray-700">{stats.total}</div>
              <div className="text-sm text-gray-500">Total Personnel</div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <div className="text-2xl font-bold text-green-700">{stats.compliant}</div>
              </div>
              <div className="text-sm text-green-600">Compliant</div>
            </CardContent>
          </Card>
          <Card className={stats.nonCompliant > 0 ? "bg-yellow-50 border-yellow-300" : "bg-white border-gray-200"}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                {stats.nonCompliant > 0 && <AlertTriangle className="h-5 w-5 text-yellow-600" />}
                <div className={`text-2xl font-bold ${stats.nonCompliant > 0 ? "text-yellow-700" : "text-gray-700"}`}>
                  {stats.nonCompliant}
                </div>
              </div>
              <div className={`text-sm ${stats.nonCompliant > 0 ? "text-yellow-600" : "text-gray-500"}`}>Non-Compliant</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white border-gray-200 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Type</Label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-36 h-9" data-testid="type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="Caregiver">Caregiver</SelectItem>
                    <SelectItem value="RN">RN</SelectItem>
                    <SelectItem value="OfficeStaff">Office Staff</SelectItem>
                    <SelectItem value="Contractor">Contractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-32 h-9" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="OnLeave">On Leave</SelectItem>
                    <SelectItem value="Terminated">Terminated</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Label className="text-sm text-gray-600">As of Date</Label>
                <Input
                  type="date"
                  value={asOfDate}
                  onChange={(e) => setAsOfDate(e.target.value)}
                  className="w-40 h-9"
                  data-testid="as-of-date-input"
                />
              </div>

              <Button variant="outline" size="sm" onClick={fetchData} className="h-9">
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>

              <div className="ml-auto">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleExportCSV} 
                  disabled={exporting || loading}
                  className="h-9"
                  data-testid="export-csv-btn"
                >
                  <Download className={`h-4 w-4 mr-1 ${exporting ? 'animate-pulse' : ''}`} />
                  Export CSV
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Matrix Table */}
        <Card className="bg-white border-gray-200">
          <CardHeader className="border-b border-gray-200">
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <Users className="h-5 w-5 text-blue-600" />
              Training Compliance Matrix
              <span className="text-sm font-normal text-gray-500 ml-2">
                {matrixData.rows.length} personnel × {matrixData.requirement_codes.length} requirements
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-gray-500">
                <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading matrix...
              </div>
            ) : matrixData.rows.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                No personnel found matching filters
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="font-semibold text-gray-700 sticky left-0 bg-gray-50 z-10 min-w-[200px]">
                        Personnel
                      </TableHead>
                      <TableHead className="font-semibold text-gray-700 min-w-[100px]">Type</TableHead>
                      <TableHead className="font-semibold text-gray-700 min-w-[100px]">Status</TableHead>
                      <TableHead className="font-semibold text-gray-700 min-w-[100px]">Compliant</TableHead>
                      {matrixData.requirement_codes.map((code) => (
                        <TableHead key={code} className="font-semibold text-gray-700 text-center min-w-[90px]">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger className="cursor-help">
                                <span className="text-xs">{code}</span>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{getReqName(code)}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matrixData.rows.map((row) => (
                      <TableRow 
                        key={row.personnel_id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/personnel/${row.personnel_id}?tab=compliance&date=${asOfDate}`)}
                        data-testid={`matrix-row-${row.personnel_id}`}
                      >
                        <TableCell className="font-medium text-gray-900 sticky left-0 bg-white">
                          {row.personnel_name}
                        </TableCell>
                        <TableCell className="text-gray-700">
                          {TYPE_LABELS[row.personnel_type] || row.personnel_type}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={
                              row.personnel_status === "Active" ? "bg-green-50 text-green-700 border-green-200" :
                              row.personnel_status === "OnLeave" ? "bg-yellow-50 text-yellow-700 border-yellow-200" :
                              "bg-gray-100 text-gray-600 border-gray-200"
                            }
                          >
                            {row.personnel_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {row.is_compliant ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-sm">Yes</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 text-yellow-600">
                              <AlertTriangle className="h-4 w-4" />
                              <span className="text-sm">No</span>
                            </div>
                          )}
                        </TableCell>
                        {matrixData.requirement_codes.map((code) => (
                          <TableCell key={code} className="text-center">
                            {renderStatusBadge(row.requirements[code], code)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="bg-white border-gray-200 mt-6 max-w-2xl">
          <CardContent className="p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Legend</h4>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGE_COLORS["Valid"]}>
                  <CheckCircle className="h-3 w-3 mr-1" /> Valid
                </Badge>
                <span className="text-xs text-gray-500">Current and not expiring soon</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGE_COLORS["ExpiringSoon"]}>
                  <AlertTriangle className="h-3 w-3 mr-1" /> Soon
                </Badge>
                <span className="text-xs text-gray-500">Expiring within 30 days</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGE_COLORS["Expired"]}>
                  <XCircle className="h-3 w-3 mr-1" /> Expired
                </Badge>
                <span className="text-xs text-gray-500">Past expiration date</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={STATUS_BADGE_COLORS["Missing"]}>
                  -
                </Badge>
                <span className="text-xs text-gray-500">Missing - never completed</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default TrainingMatrixPage;
